/* eslint-disable no-unused-expressions */
import RGL, { Layout, WidthProvider } from "react-grid-layout";
import styled from "styled-components";
import { ReactNode } from "react";

const ReactGridLayout = WidthProvider(RGL);

interface WidgetItem {
  layout: Layout;
  title?: string;
  widget: ReactNode;
}

interface MainGridLayoutProps {
  layout?: Layout[];
  widgets?: WidgetItem[];
  onLayoutChange?: (layout: Layout[]) => void;
}

interface WidgetProps {
  className?: string;
  item: WidgetItem;
  children: ReactNode;
}

const MainGridLayout = ({
  layout = [],
  widgets = [],
  onLayoutChange,
}: MainGridLayoutProps) => {
  return (
    <ReactGridLayout
      layout={layout}
      className="layout"
      rowHeight={30}
      cols={12}
      margin={[13, 13]}
      isDraggable={true}
      isResizable={true}
      onLayoutChange={onLayoutChange}
      draggableHandle=".widgetHandle"
      // The widget body owns its own scrolling, so it must never start a drag.
      // Excluding it here (rather than making only the title a handle) keeps the
      // widgets that render their own internal title draggable by their frame.
      draggableCancel=".widget-body"
    >
      {widgets.map((item) => {
        return (
          <div key={item.layout.i}>
            <StyledWidget item={item}>{item.widget}</StyledWidget>
          </div>
        );
      })}
    </ReactGridLayout>
  );
};

const Widget = ({ className, item, children }: WidgetProps) => {
  return (
    <div className={`${className} widgetHandle`}>
      <div className="widget-container">
        {item.title && <div className="widget-title">{item.title}</div>}
        <div className="widget-body">{children}</div>
      </div>
    </div>
  );
};

interface StyledWidgetProps {
  theme: {
    palette: {
      divider: string;
    };
  };
}

const StyledWidget = styled(Widget)`
  /* border-box is not inherited from the #root rule in GlobalStyle, so the
     padding and border below would otherwise push the widget past its grid item. */
  box-sizing: border-box;
  display: flex;
  height: 100%;
  min-width: 0;
  min-height: 0;
  z-index: 1000;
  border: 1px solid ${(props: StyledWidgetProps) => props.theme.palette.divider};
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s ease-in-out;
  background-color: transparent;

  &:hover {
    cursor: pointer;
  }

  .widget-container,
  .widget-title,
  .widget-body {
    box-sizing: border-box;
  }

  .widget-container {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    /* Without these a flex child refuses to shrink below its content, which
       stops .widget-body from ever bounding its overflow. */
    min-width: 0;
    min-height: 0;
  }

  .widget-title {
    flex: 0 0 auto;
    font-size: 1rem;
    font-weight: bold;
    margin-bottom: 5px;
  }

  /* Widget roots come in two modes, and the choice decides whether the widget
     scrolls or fills:
       - dense (DenseBarChart, CaseTypesPerUserWidget): intrinsic pixel height from
         the row count, root is "flex: 0 0 auto", overflows and scrolls in here.
       - fill (CaseDataChart, TimeTrackedPerUserWidget): root is "flex: 1 1 auto"
         with height "100%", sized by this box and never scrolling it.
     A dense widget whose root both shrinks (min-height: 0) and clips
     (overflow: hidden) silently drops rows with no error, so prefer building new
     dense widgets on DenseBarChart, which owns the correct mode. */
  .widget-body {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;
    overflow: auto;
    /* Reserve the scrollbar gutter up front. Without it a chart measured at the
       full body width overflows horizontally the moment the vertical scrollbar
       appears, producing a spurious horizontal scrollbar. */
    scrollbar-gutter: stable;
    cursor: default;
  }
`;

export default MainGridLayout;
